import { useMemo, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';

export const useQuery = (): URLSearchParams => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

export const useNonNullQueryParams = <T = Record<string, string>>(): T => {
  const paramsIter = useQuery().entries();
  const searchParams: T = {} as T;
  for (const [k, v] of paramsIter) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    searchParams[k] = v;
  }
  return searchParams;
};

interface UseQueryParamsResponse {
  params: URLSearchParams;
  changeParam: (paramName: string, paramValue: string) => void;
  setParams: (
    nextInit: URLSearchParams | ((prev: URLSearchParams) => URLSearchParams)
  ) => void;
}

export const useQueryParams = (): UseQueryParamsResponse => {
  const params = useQuery();
  const history = useHistory();
  const location = useLocation();

  const changeParam = useCallback(
    (paramName: string, paramValue: string) => {
      const params = new URLSearchParams(location.search);
      params.set(paramName, paramValue);
      history.replace({
        pathname: location.pathname,
        search: `?${params.toString()}`,
      });
    },
    [history, location]
  );
  const setParams = useCallback(
    (
      nextInit: URLSearchParams | ((prev: URLSearchParams) => URLSearchParams)
    ) => {
      const params = new URLSearchParams(window.location.search);
      if (typeof nextInit === 'function') {
        history.replace({
          pathname: location.pathname,
          search: `?${nextInit(params).toString()}`,
        });
      } else {
        history.replace({
          pathname: location.pathname,
          search: `?${nextInit.toString()}`,
        });
      }
    },
    [history, location]
  );

  return { params, changeParam, setParams };
};
